import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { ChapterButtonsLocation, LinkTypeIds, prefixUrl } from 'GeminiViewerComponent/helpers';
import { config } from 'gemini-config';

const initialState = {
    panelOpen: false,
    allSimilarTypeLinks: [],
    linkDisplay: false,
    linkContent: null,
    linkData: null,
    linkTypeId: null,
    overlayText: null,
    scenesOpen: false,
    marziLoaded: false,
    viewFilters: {
        tags: {
            zone_tags: [],
            item_tags: [],
        },
    },
    isChecklistLoaded: false,
    procedureContentVersion: null,
    procedureLoaded: false,
    autoPlay: false,
    procedureSavedView: null,
    videoPlayback: {
        requireVideoPlayback: false,
        videoPlaybackEndMargin: 0,
    },
    showOnlyByFilter: false,
    showAllTags: false,
    tagsNameDisplay: false,
    tagsContainerDisplay: false,
    isAssetLoaded: false,
    loadedAssetType: null,
    sessionsTabIndex: 0,
    isAssociateAssetLoaded: false,
    isEmptyHotspotVisible: true,
};

export const findVideoChapter = (node, contents = []) => {
    let chapters = [];
    let chapterData = [];
    if (contents && contents?.length > 0) {
        const contentData = contents.find((content) => content.content_id === node?.content_id);
        if (contentData?.content_versions?.length > 0) {
            if (node?.content_version_id) {
                const contentVersions = contentData?.content_versions?.find(
                    (version) => version.content_version_id === node?.content_version_id
                );
                if (contentVersions?.chapters?.length > 0) {
                    chapters = contentVersions?.chapters;
                }
            } else {
                if ([...contentData.content_versions]?.pop()?.chapters?.length > 0) {
                    chapters = [...contentData.content_versions]?.pop()?.chapters;
                }
            }
        }
    }
    if (chapters && chapters.length > 0) {
        chapterData = chapters.find((chapter) => chapter.chapter_id === node.chapter_id);
    }
    return chapterData;
};

export const showNodeContent = createAsyncThunk(
    'viewerVisibility/showNodeContent',
    async ({ contentService, node }, { getState, rejectWithValue, dispatch }) => {
        if (node.content_url && node.content_link_type_id) {
            const url = prefixUrl(node.content_url, getState().accounts.activeUser.s3_prefix);
            if (config?.modeType === 'author') {
                let contentData = [];
                if (node?.content_link_type_id === LinkTypeIds.Video) {
                    const content = await contentService.fetchContentById(
                        node.content_id,
                        getState().accounts.activeUser.s3_prefix
                    );
                    contentData = content;
                }
                return {
                    last_content_version: { url: url },
                    default_link_type_id: node.content_link_type_id,
                    auto_play: node.auto_play,
                    show_chapters: node?.show_chapters,
                    collapse_chapters: node?.collapse_chapters,
                    chapter_buttons_location: node?.chapter_buttons_location,
                    chapters: node?.show_chapters && contentData?.last_content_version?.chapters,
                    require_video_playback: node?.require_video_playback,
                    video_playback_end_margin: node?.video_playback_end_margin,
                    content_skill_id: node?.content_skill_id,
                    chapter_id: node?.chapter_id || null,
                    caption_languages: contentData?.last_content_version?.caption_languages,
                    display_name: contentData?.display_name,
                };
            } else {
                const chapter_data = findVideoChapter(node, getState().asset?.dependencies?.content);
                let lastContentVersion = null;
                let display_name = null;
                if (getState().asset?.dependencies?.content && getState().asset?.dependencies?.content?.length > 0) {
                    const content = getState().asset?.dependencies?.content.find(
                        (content) => content.content_id === node.content_id
                    );
                    display_name = content?.display_name;
                    if (Array.isArray(content?.content_versions) && content.content_versions.length > 0) {
                        lastContentVersion = content.content_versions.toSorted((a, b) => b.version - a.version)[0];
                    }
                }
                return {
                    last_content_version: { url: url },
                    default_link_type_id: node.content_link_type_id,
                    auto_play: node.auto_play,
                    show_chapters: node?.show_chapters,
                    collapse_chapters: node?.collapse_chapters,
                    chapter_buttons_location: node?.chapter_buttons_location,
                    chapters: (node?.show_chapters && lastContentVersion?.chapters) || null,
                    require_video_playback: node?.require_video_playback,
                    video_playback_end_margin: node?.video_playback_end_margin,
                    content_skill_id: node?.content_skill_id,
                    chapter_data: chapter_data || null,
                    caption_languages: lastContentVersion?.caption_languages,
                    display_name,
                };
            }
        }

        if (contentService || config.modeType === 'author') {
            var content = await contentService.fetchContentById(
                node.content_id,
                getState().accounts.activeUser.s3_prefix
            );
            content.auto_play = node.auto_play;
            return content;
        } else {
            if (getState().asset?.dependencies?.content && getState().asset?.dependencies?.content?.length > 0) {
                content = getState().asset?.dependencies?.content.find(
                    (content) => content.content_id === node.content_id
                );
                let updatedContent = { ...content };
                updatedContent.default_link_type_id =
                    updatedContent?.default_link_type_id || updatedContent?.link_type_id;

                return updatedContent;
            }
        }

        return rejectWithValue('Could not load node content');
    }
);

const viewerVisibilitySlice = createSlice({
    name: 'viewerVisibility',
    initialState,
    reducers: {
        setTagFilters: (state, action) => {
            state.viewFilters.tags = action.payload;
        },
        setScenesOpen: (state, action) => {
            return { ...state, scenesOpen: action.payload };
        },
        setPanelVis: (state, action) => {
            return { ...state, panelOpen: action.payload.open };
        },
        setLinkDisplayVis: (state, action) => {
            const uniqueContentLinks = action.payload.allLinks
                ? action.payload.allLinks.reduce((acc, current) => {
                      const x = acc.find(
                          (item) =>
                              (item.content_version === 'object' &&
                                  current.content_version === 'object' &&
                                  item.content_version.content_version_id ===
                                      current.content_version.content_version_id) ||
                              (item.content === 'object' &&
                                  current.content === 'object' &&
                                  item.content.content_id === current.content.content_id) ||
                              item.content_id === current.content_id
                      );
                      if (!x) {
                          return acc.concat([current]);
                      } else {
                          return acc;
                      }
                  }, [])
                : [];

            return {
                ...state,
                linkDisplay: action.payload.open,
                linkContent: action.payload.linkContent,
                linkData: {
                    ...action.payload.linkData,
                    chapter_buttons_location:
                        typeof action.payload.linkData?.chapter_buttons_location === 'number'
                            ? ChapterButtonsLocation[action.payload.linkData.chapter_buttons_location]
                            : action.payload.linkData?.chapter_buttons_location,
                },
                allSimilarTypeLinks: action.payload.allLinks,
                allUniqueContentLinks: uniqueContentLinks,
                linkTypeId: action.payload.linkTypeId,
                autoPlay: action.payload?.autoPlay,
                chapter_id: action.payload?.chapter_id,
                chapter_data: action.payload?.chapter_data,
                overlayText:
                    action.payload.linkTypeId === LinkTypeIds.Image && action.payload?.overlayText
                        ? JSON.parse(action.payload?.overlayText)
                        : null,
            };
        },
        displayProcedureContentVersion: (state, action) => {
            state.procedureContentVersion = action.payload;
            state.procedureLoaded = action?.payload ? true : false;
        },
        setProcedureSavedView: (state, action) => {
            if (state.procedureSavedView) return;
            state.procedureSavedView = action.payload;
        },
        clearProcedureSavedView: (state) => {
            state.procedureSavedView = null;
        },
        setMarziLoaded: (state, action) => {
            return { ...state, marziLoaded: true };
        },
        resetVisibilityState: (state) => {
            return initialState;
        },
        loadChecklist: (state, action) => {
            state.isChecklistLoaded = action.payload;
        },
        setShowOnlyByFilter: (state, action) => {
            state.showOnlyByFilter = !state.showOnlyByFilter;
        },
        setShowAllTags: (state, action) => {
            state.showAllTags = !state.showAllTags;
        },
        setTagsNameDisplay: (state, action) => {
            state.tagsNameDisplay = !state.tagsNameDisplay;
        },
        setTagsContainerDisplay: (state, action) => {
            state.tagsContainerDisplay = !state.tagsContainerDisplay;
        },
        loadAsset: (state, action) => {
            state.isAssetLoaded = action.payload;
        },
        setAssetType: (state, action) => {
            state.loadedAssetType = action.payload;
        },
        setSessionsTabIndex: (state, action) => {
            state.sessionsTabIndex = action.payload;
        },
        toggleContentValidationModal: (state) => {
            return {
                ...state,
                contentValidationModal: !state.contentValidationModal,
            };
        },
        setContentValidationModalContent: (state, action) => {
            state.contentValidationModalContent = action.payload;
        },
        setChapterData: (state, action) => {
            state.chapter_data = action.payload;
        },
        loadAssociateAsset: (state, action) => {
            state.isAssociateAssetLoaded = action.payload;
        },
        setIsEmptyHotspotVisible: (state, action) => {
            state.isEmptyHotspotVisible = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(showNodeContent.fulfilled, (state, action) => {
            return {
                ...state,
                linkDisplay: true,
                linkContent:
                    action.payload.last_content_version?.embed_data
                        ? action.payload.last_content_version.embed_data
                        : action.payload.last_content_version?.encoding_status?.toLowerCase() === 'complete'
                        ? action.payload.last_content_version?.encoded_url
                        : action.payload.last_content_version?.url,
                linkData:
                    action.payload?.default_link_type_id === LinkTypeIds.SME ||
                    action.payload?.default_link_type_id === LinkTypeIds.Video
                        ? {
                              ...action.payload,
                              chapter_buttons_location:
                                  typeof action.payload?.chapter_buttons_location === 'number'
                                      ? ChapterButtonsLocation[action.payload.chapter_buttons_location]
                                      : action.payload?.chapter_buttons_location,
                          }
                        : {
                              skill_id: action.payload.content_skill_id,
                              display_name: action.payload.display_name,
                          },
                linkTypeId: action.payload.default_link_type_id,
                autoPlay: action.payload.auto_play,
                overlayText: action.payload.last_content_version?.overlay_text,
                chapter_id: action.payload?.chapter_id,
                chapter_data: action.payload?.chapter_data,
                videoPlayback: {
                    requireVideoPlayback: !!action.payload?.require_video_playback,
                    videoPlaybackEndMargin: action.payload?.video_playback_end_margin,
                },
            };
        });
    },
});

export const getPanelVis = (state) => state.viewerVisibility.panelOpen;
export const getLinkAutoPlay = (state) => state.viewerVisibility.autoPlay === true || state.viewerVisibility.autoPlay === 'true';
export const getLinkOverlayText = (state) => state.viewerVisibility.overlayText;
export const getLinkVideoPlayback = (state) => state.viewerVisibility.videoPlayback;
export const getLinkDisplayVis = (state) => state.viewerVisibility.linkDisplay;
export const getLinkDisplayContent = (state) => state.viewerVisibility.linkContent;
export const getLinkDisplayData = (state) => state.viewerVisibility.linkData;
export const getProcedureContentVersion = (state) => {
    return state.viewerVisibility.procedureContentVersion;
};
export const getProcedureLoaded = (state) => state.viewerVisibility.procedureLoaded;
export const getlLinkTypeId = (state) => state.viewerVisibility.linkTypeId;
export const getScenesOpen = (state) => state.viewerVisibility.scenesOpen;
export const getMarziLoaded = (state) => state.viewerVisibility.marziLoaded;
export const getTagFilters = (state) => state.viewerVisibility.viewFilters.tags;
export const getShowOnlyByFilter = (state) => state.viewerVisibility.showOnlyByFilter;
export const getTagsNameDisplay = (state) => state.viewerVisibility.tagsNameDisplay;
export const getTagsContainerDisplay = (state) => state.viewerVisibility.tagsContainerDisplay;
export const getShowAllTags = (state) => state.viewerVisibility.showAllTags;
export const getIsChecklistLoaded = (state) => state.viewerVisibility.isChecklistLoaded;
export const getIsAssetLoaded = (state) => state.viewerVisibility.isAssetLoaded;
export const getIsAssociateAssetLoaded = (state) => state.viewerVisibility.isAssociateAssetLoaded;
export const getLoadedAssetType = (state) => state.viewerVisibility.loadedAssetType;
export const getSessionsTabIndex = (state) => state.viewerVisibility.sessionsTabIndex;
export const getChapterId = (state) => state.viewerVisibility.chapter_id;
export const getChapterData = (state) => state.viewerVisibility.chapter_data;
export const getAllSimilarLinks = (state) => state.viewerVisibility.allSimilarTypeLinks;
export const getUniqueContentLinks = (state) => state.viewerVisibility.allUniqueContentLinks;
export const getIsEmptyHotspotVisible = (state) => state.viewerVisibility.isEmptyHotspotVisible;
export const getProcedureSavedView = (state) => state.viewerVisibility.procedureSavedView;

export const {
    resetVisibilityState,
    setPanelVis,
    setLinkDisplayVis,
    setMarziLoaded,
    setScenesOpen,
    setTagFilters,
    loadChecklist,
    displayProcedureContentVersion,
    setProcedureSavedView,
    clearProcedureSavedView,
    setShowOnlyByFilter,
    setShowAllTags,
    setTagsNameDisplay,
    setTagsContainerDisplay,
    loadAsset,
    setAssetType,
    setSessionsTabIndex,
    setChapterData,
    loadAssociateAsset,
    setIsEmptyHotspotVisible,
} = viewerVisibilitySlice.actions;

export default viewerVisibilitySlice.reducer;
